import { useEffect, useMemo, useState } from "react";
import "./Supervisor.css";
import { useAuth, useRequireRole } from "../../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import { ChevronLeft } from "lucide-react";
import ProjectCard from "../../components/ProjectCard/ProjectCard";
import ErrorState from "../../components/ErrorState/ErrorState";
import SubordinatesOverview from "../../components/SubordinatesOverview/SubordinatesOverview";
import { useDebounce } from "../../customHooks/useDebounce";
import HeaderComponent from "../../components/HeaderComponent/HeaderComponent";
import SortSelect, { SortKey } from "../../components/SortSelect/SortSelect";
import { dynamicSort } from "../../utils/dynamicSort";
import { filterBySearch } from "../../utils/filterBySearch";
import { useSupervisor } from "../../customHooks/useSupervisor";

function Supervisor() {
  const { isSupervisor } = useAuth();
  useRequireRole(isSupervisor);

  const {
    selectedSubordinateId,
    setSelectedSubordinateId,
    subordinateStatuses,
    projectChecks,
    hasChanges,
    subordinateLoading,
    projectChecksLoading,
    fetchError,
    saveError,
    saveProjectChecks,
    updateCheck,
    confirmAll,
  } = useSupervisor();

  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearch = useDebounce(searchInput, 300);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [sortBy, setSortBy] = useState<SortKey>("clientName");
  const [thenBy, setThenBy] = useState<SortKey | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const subordinateNameInParams = searchParams.get("subordinate");

    if (subordinateNameInParams && subordinateStatuses.length > 0) {
      const subordinate = subordinateStatuses.find(
        (s) => s.displayName.toLowerCase() === subordinateNameInParams.toLowerCase(),
      );
      if (subordinate) {
        setSelectedSubordinateId(subordinate.id);
      }
    }
  }, [searchParams, subordinateStatuses, setSelectedSubordinateId]);

  const filteredProjectChecks = useMemo(() => {
    let result = [...projectChecks];

    result = dynamicSort(result, sortBy, thenBy);

    if (!debouncedSearch.trim()) return result;

    const term = debouncedSearch.toLowerCase();

    return filterBySearch(term, result);
  }, [projectChecks, debouncedSearch, sortBy, thenBy]);

  if (fetchError && !subordinateLoading) {
    return (
      <div className="supervisor-page">
        <ErrorState type="fetch" onAction={() => navigate("/supervisor")} fullPage />
      </div>
    );
  }

  return (
    <>
      <title>Projektkontrolle - Vorgesetzter</title>
      <LoadingSpinner loading={subordinateLoading} />
      <div className="supervisor-page">
        <HeaderComponent
          pageTitle={`Projektkontrolle durch Vorgesetzter
              ${
                subordinateStatuses.find((s) => s.id === selectedSubordinateId)?.displayName !==
                  undefined && !isSidebarOpen
                  ? ` - ${subordinateStatuses.find((s) => s.id === selectedSubordinateId)?.displayName}`
                  : ""
              }`}
          backDestination="/"
          hasTimePeriodPicker
          hasSearchBar
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          entityToSearch="Projekte"
        />

        <div className="content-base supervisor-content">
          <div className={`subordinates-selection ${isSidebarOpen ? "open" : "closed"}`}>
            <button className="toggle-btn" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <ChevronLeft
                size={30}
                style={{
                  transform: isSidebarOpen ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.3s",
                }}
              />
            </button>

            <SubordinatesOverview
              selectedSubordinateId={selectedSubordinateId}
              setSelectedSubordinateId={setSelectedSubordinateId}
              subordinateStatuses={subordinateStatuses}
            />
          </div>
          <div className="subordinates-checks">
            <div className="supervisor-project-checks-list">
              <SortSelect
                sortBy={sortBy}
                setSortBy={setSortBy}
                thenBy={thenBy}
                setThenBy={setThenBy}
              />
              <LoadingSpinner loading={projectChecksLoading} isFullscreen={false} />
              {!fetchError &&
                !projectChecksLoading &&
                filteredProjectChecks.map((c) => (
                  <ProjectCard
                    key={c.projectNumber}
                    variant="supervisor"
                    projectCheck={c}
                    onSupervisorChange={(id, value, type) => updateCheck(id, value, type)}
                  />
                ))}
              {!projectChecksLoading && !fetchError && filteredProjectChecks.length === 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <ErrorState type="empty" />
                </div>
              )}
            </div>

            {!projectChecksLoading && !fetchError && projectChecks.length !== 0 && (
              <div className="supervisor-footer">
                <button className="supervisor-confirm-all" onClick={confirmAll}>
                  Alle Projekte bestätigen
                </button>

                {saveError && <ErrorState type="save" />}

                <div className="supervisor-footer-right">
                  <button
                    className="supervisor-save"
                    onClick={saveProjectChecks}
                    disabled={!hasChanges}
                  >
                    Speichern
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Supervisor;
